import { BrollAsset, ClipSegment, LibraryClipLabel, VideoClip } from 'services/VideoEditingService';

/**
 * Library b-roll inside an edit project.
 *
 * A pulled-in clip is stored twice, on purpose:
 *  - a `BrollAsset` — the record of WHICH library clip, and the only authoritative part (the id);
 *  - a `ClipSegment` — the draggable thing, so placing, trimming and previewing it go through the
 *    machinery that already exists for sub-clips carved from a project's own files, rather than a
 *    parallel code path that would have to be kept in step.
 *
 * Their source files are NOT merged into the project's clip list: that list drives the Video and
 * Audio tabs, and a clip from another creator's folder appearing there would be wrong. They are
 * merged only into the id→clip map the timeline resolves playback through.
 */

// Deterministic, so re-adding the same library clip can't create a second segment and removing
// the asset can find its segment without a lookup table.
export function brollSegmentId(labelId: string) {
  return `broll:${labelId}`;
}

export function isBrollSegmentId(segmentId: string) {
  return segmentId.startsWith('broll:');
}

export function brollAssetFrom(label: LibraryClipLabel): BrollAsset {
  return {
    labelId: label.labelId,
    fileId: label.fileId,
    fileName: label.fileName,
    name: label.name,
    inSec: label.inSec,
    outSec: label.outSec,
  };
}

/**
 * The draggable segment for one pulled-in clip.
 *
 * A label with no range covers its whole file, and a segment needs a concrete one — so fall back
 * to the clip's duration. `durationSec` can be null when Drive hasn't reported it, in which case
 * there is no honest range to state and the caller gets null rather than a zero-length segment.
 */
export function brollSegmentFrom(label: LibraryClipLabel): ClipSegment | null {
  const inSec = label.inSec ?? 0;
  const outSec = label.outSec ?? label.durationSec;
  if (outSec == null || outSec <= inSec) return null;
  return {
    segmentId: brollSegmentId(label.labelId),
    folderId: label.sourceFolderId || '',
    sourceFileId: label.fileId,
    fileName: label.fileName,
    name: label.name || label.fileName,
    kind: 'video',
    inSec,
    outSec,
  };
}

// The source file of a pulled-in clip, shaped as a VideoClip so the timeline can resolve its
// playback through the same id→clip map as everything else.
export function brollSourceClipFrom(label: LibraryClipLabel): VideoClip {
  return {
    fileId: label.fileId,
    name: label.fileName || label.fileId,
    mimeType: label.mimeType || 'video/mp4',
    sizeBytes: null,
    durationSec: label.durationSec,
    width: null,
    height: null,
    createdTime: null,
    streamUrl: label.streamUrl,
  };
}
